/**
 * What a mark may be made of — `docs/marks-and-icons.md` §1.
 *
 * **Here rather than in the client** because both sides have to agree: the
 * picker offers these and the API refuses anything else (§9 — "refused in the
 * UI and by the API"). The *shapes* stay in `client/src/frame/mark-glyphs.tsx`,
 * which is the only part of this that is drawing rather than vocabulary; that
 * file takes its slug list from here, so a glyph cannot exist in one place and
 * not the other.
 *
 * A slug is a database value. These never change once shipped; a glyph that is
 * retired keeps its entry, because a row holding it would otherwise draw
 * nothing.
 */
/** What kind of work it is — the glyph on every job in that division. */
export const DIVISION_GLYPH_SLUGS = [
    'leaf',
    'mower',
    'flower',
    'tree',
    'hedge',
    'drop',
    'sprinkler',
    'bricks',
    'fence',
    'bulb',
    'snow',
    'shovel',
    'rake',
    'grass',
    'bug',
    'spray',
    'excavator',
    'pipe',
    'wheelbarrow',
    'wrench',
    'truck',
    'star',
    'pool',
    'broom',
];
/** What kind of machine it is — picked on the type, never on the machine. */
export const EQUIPMENT_GLYPH_SLUGS = [
    'pickup',
    'dump',
    'van',
    'trailer',
    'enclosed',
    'dumptrailer',
    'zturn',
    'walkbehind',
    'standon',
    'trimmer',
    'hedgetrimmer',
    'chainsaw',
    'blower',
    'backpack',
    'edger',
    'aerator',
    'skid',
    'miniex',
    'tractor',
    'plow',
    'spreader',
    'generator',
    'washer',
    'sprayer',
    'chipper',
    'plate',
    'auger',
    'ladder',
    'toolbox',
];
/**
 * The ten a mark may be tinted, and nothing else.
 *
 * Hex because `divisions.colour` has been a hex column since migration 0026 and
 * a rename would be churn for nothing. Migration 0103's CHECK on
 * `equipment_types.colour` is the same ten;
 * `tests/marks.test.ts` fails if the two lists drift apart, which is the only
 * way to keep a constant in SQL honest.
 */
export const MARK_PALETTE = {
    green: '#2e7d4f',
    amber: '#b0731a',
    blue: '#2b66a8',
    purple: '#7a4fb0',
    teal: '#1f8a8a',
    indigo: '#5b6fb5',
    red: '#b42318',
    cyan: '#0e7490',
    orange: '#b45309',
    slate: '#7b8794',
};
export const MARK_COLOUR_NAMES = Object.keys(MARK_PALETTE);
export const MARK_PALETTE_HEXES = Object.values(MARK_PALETTE);
/** What a row falls back to when its icon or colour is missing (§4). */
export const FALLBACK_GLYPH = 'wrench';
export const FALLBACK_COLOUR = MARK_PALETTE.slate;
/** The icon a family starts with, until somebody picks one by hand. */
export const EQUIPMENT_FAMILY_DEFAULTS = {
    truck: 'pickup',
    trailer: 'trailer',
    mower: 'zturn',
    handheld: 'trimmer',
    loader: 'skid',
    attachment: 'plow',
    other: 'toolbox',
};
/** And the colour, from board 3's families. The same table 0103 backfilled. */
export const EQUIPMENT_FAMILY_COLOURS = {
    truck: MARK_PALETTE.blue,
    trailer: MARK_PALETTE.indigo,
    mower: MARK_PALETTE.green,
    handheld: MARK_PALETTE.amber,
    loader: MARK_PALETTE.orange,
    attachment: MARK_PALETTE.teal,
    other: MARK_PALETTE.slate,
};
export const isDivisionGlyph = (slug) => !!slug && DIVISION_GLYPH_SLUGS.includes(slug);
export const isEquipmentGlyph = (slug) => !!slug && EQUIPMENT_GLYPH_SLUGS.includes(slug);
export const isPaletteColour = (hex) => !!hex && MARK_PALETTE_HEXES.includes(hex);
//# sourceMappingURL=marks.js.map